/**
 * Engraving Overlay System
 *
 * A reusable JavaScript module for overlaying transparent letter PNGs
 * on product images with variant change persistence.
 *
 * Usage:
 * 1. Include this script on product pages with engraving enabled
 * 2. Add the engraving-letter-selector snippet to display letter buttons
 * 3. Configure overlay positioning via data attributes on the selector element
 *
 * @version 1.0.0
 * @author DÉSIR Studio
 */

class EngravingOverlay {
  /**
   * Initialize the Engraving Overlay system
   * @param {Object} options - Configuration options
   * @param {string} options.selectorId - ID of the letter selector container
   * @param {string} options.gallerySelector - Selector for the media gallery
   * @param {string} options.formInputSelector - Selector for the hidden form input
   * @param {string} options.assetUrlBase - Base URL for letter PNG assets
   */
  constructor(options = {}) {
    // Default configuration
    this.config = {
      selectorId: options.selectorId || 'engraving-letter-selector',
      gallerySelector: options.gallerySelector || 'media-gallery',
      formInputSelector: options.formInputSelector || '.engraving-letter-input',
      assetUrlBase: options.assetUrlBase || '',
      overlayPositionTop: options.overlayPositionTop || '50%',
      overlayPositionLeft: options.overlayPositionLeft || '50%',
      overlayScaleRound: options.overlayScaleRound || 1,
      overlayScaleRectangle: options.overlayScaleRectangle || 1,
      transitionDuration: options.transitionDuration || 300
    };

    // State
    this.selectedLetter = null;
    this.selectedFont = 'script'; // Default font is Script
    this.isRectangleProduct = false; // Whether product has "rectangle" tag
    this.isInitialized = false;
    this.variantChangeUnsubscriber = null;

    // DOM elements (will be set during init)
    this.selectorContainer = null;
    this.gallery = null;
    this.formInput = null;
    this.letterButtons = null;
    this.fontButtons = null;

    // Bind methods
    this.handleLetterSelect = this.handleLetterSelect.bind(this);
    this.handleFontSelect = this.handleFontSelect.bind(this);
    this.handleVariantChange = this.handleVariantChange.bind(this);
    this.updateOverlay = this.updateOverlay.bind(this);
  }

  /**
   * Initialize the overlay system
   * Call this after DOM is ready
   */
  init() {
    // Get selector container
    this.selectorContainer = document.getElementById(this.config.selectorId);
    if (!this.selectorContainer) {
      console.warn('EngravingOverlay: Selector container not found');
      return;
    }

    // Read configuration from data attributes
    this.readConfigFromDataAttributes();

    // Get media gallery
    this.gallery = document.querySelector(this.config.gallerySelector);
    if (!this.gallery) {
      console.warn('EngravingOverlay: Media gallery not found');
      return;
    }

    // Get form input for cart line item property
    this.formInput = document.querySelector(this.config.formInputSelector);

    // Get letter buttons
    this.letterButtons = this.selectorContainer.querySelectorAll('[data-engraving-letter]');
    if (!this.letterButtons.length) {
      console.warn('EngravingOverlay: No letter buttons found');
      return;
    }

    // Get font buttons
    this.fontButtons = this.selectorContainer.querySelectorAll('[data-engraving-font]');

    // Read default font from data attribute
    if (this.selectorContainer.dataset.defaultFont) {
      this.selectedFont = this.selectorContainer.dataset.defaultFont;
    }

    // Setup event listeners
    this.setupEventListeners();

    // Subscribe to variant change events
    this.subscribeToVariantChanges();

    // Add overlay containers to media items
    this.injectOverlayContainers();

    // Set default state (No Engraving)
    this.setDefaultState();

    this.isInitialized = true;
    console.log('EngravingOverlay: Initialized successfully');
  }

  /**
   * Read configuration from data attributes on selector container
   */
  readConfigFromDataAttributes() {
    const container = this.selectorContainer;

    if (container.dataset.overlayTop) {
      this.config.overlayPositionTop = container.dataset.overlayTop;
    }
    if (container.dataset.overlayLeft) {
      this.config.overlayPositionLeft = container.dataset.overlayLeft;
    }
    // Read both scale values
    if (container.dataset.overlayScaleRound) {
      this.config.overlayScaleRound = parseFloat(container.dataset.overlayScaleRound);
    }
    if (container.dataset.overlayScaleRectangle) {
      this.config.overlayScaleRectangle = parseFloat(container.dataset.overlayScaleRectangle);
    }
    if (container.dataset.assetUrl) {
      this.config.assetUrlBase = container.dataset.assetUrl;
    }
    // Check if product has "rectangle" tag
    if (container.dataset.isRectangle) {
      this.isRectangleProduct = container.dataset.isRectangle === 'true';
    }
  }

  /**
   * Get the appropriate overlay scale based on product type
   * @returns {number} - Scale value for round or rectangle shapes
   */
  getOverlayScale() {
    return this.isRectangleProduct
      ? this.config.overlayScaleRectangle
      : this.config.overlayScaleRound;
  }

  /**
   * Setup event listeners for letter and font selection
   */
  setupEventListeners() {
    // Letter button click events
    this.letterButtons.forEach(button => {
      button.addEventListener('click', this.handleLetterSelect);
    });

    // Font button click events
    if (this.fontButtons) {
      this.fontButtons.forEach(button => {
        button.addEventListener('click', this.handleFontSelect);
      });
    }

    // Mobile letter dropdown change event
    const letterDropdown = this.selectorContainer.querySelector('[data-engraving-dropdown]');
    if (letterDropdown) {
      letterDropdown.addEventListener('change', (event) => {
        const letter = event.target.value;
        const button = this.selectorContainer.querySelector(`[data-engraving-letter="${letter}"]`);
        this.selectLetter(letter, button);
      });
    }

    // Mobile font dropdown change event
    const fontDropdown = this.selectorContainer.querySelector('[data-engraving-font-dropdown]');
    if (fontDropdown) {
      fontDropdown.addEventListener('change', (event) => {
        const font = event.target.value;
        this.selectFont(font);
      });
    }
  }

  /**
   * Subscribe to Shopify variant change events via PubSub
   */
  subscribeToVariantChanges() {
    // Check if PubSub system is available (from Shopify Dawn theme)
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this.handleVariantChange
      );
    }
  }

  /**
   * Inject overlay containers into media gallery items
   * Note: Overlay containers are now added via Liquid in product-thumbnail.liquid
   * This method updates existing overlay containers with proper positioning
   */
  injectOverlayContainers() {
    // Find all existing overlay containers (added via Liquid)
    const overlayContainers = this.gallery.querySelectorAll('.engraving-overlay-container');

    overlayContainers.forEach(container => {
      // Apply positioning from config
      container.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 5;
      `;

      // Update overlay image styling
      const image = container.querySelector('.engraving-overlay-image');
      if (image) {
        image.style.cssText = `
          display: none;
          max-width: 40%;
          max-height: 40%;
          width: auto;
          height: auto;
          object-fit: contain;
          opacity: 0;
          transition: opacity ${this.config.transitionDuration}ms ease;
          transform: translateY(${this.config.overlayPositionTop === '50%' ? '0' : this.config.overlayPositionTop}) scale(${this.getOverlayScale()});
        `;
      }
    });

    // If no overlay containers found via Liquid, create them dynamically (fallback)
    if (overlayContainers.length === 0) {
      this.createOverlayContainersFallback();
    }
  }

  /**
   * Fallback method to create overlay containers dynamically
   * Used when Liquid containers are not present
   */
  createOverlayContainersFallback() {
    const mediaItems = this.gallery.querySelectorAll('.product__media-item');

    mediaItems.forEach(item => {
      // Check if overlay container already exists
      if (item.querySelector('.engraving-overlay-container')) {
        return;
      }

      // Find the media div to position overlay within
      const mediaDiv = item.querySelector('.product__media');
      if (!mediaDiv) return;

      // Create overlay container
      const overlayContainer = document.createElement('div');
      overlayContainer.className = 'engraving-overlay-container';
      overlayContainer.setAttribute('data-engraving-overlay', '');
      overlayContainer.style.cssText = `
        position: absolute;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        pointer-events: none;
        z-index: 5;
      `;

      // Create overlay image
      const overlayImage = document.createElement('img');
      overlayImage.className = 'engraving-overlay-image';
      overlayImage.alt = 'Engraving preview';
      overlayImage.style.cssText = `
        display: none;
        max-width: 40%;
        max-height: 40%;
        width: auto;
        height: auto;
        object-fit: contain;
        opacity: 0;
        // transition: opacity ${this.config.transitionDuration}ms ease;
        transform: scale(${this.getOverlayScale()});
      `;

      overlayContainer.appendChild(overlayImage);
      mediaDiv.appendChild(overlayContainer);
    });
  }

  /**
   * Set default state (No Engraving selected)
   */
  setDefaultState() {
    const defaultButton = this.selectorContainer.querySelector('[data-engraving-letter="none"]');
    if (defaultButton) {
      this.selectButton(defaultButton);
    }
  }

  /**
   * Handle letter button selection
   * @param {Event} event - Click event
   */
  handleLetterSelect(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const letter = button.dataset.engravingLetter;

    this.selectLetter(letter, button);
  }

  /**
   * Handle font button selection
   * @param {Event} event - Click event
   */
  handleFontSelect(event) {
    event.preventDefault();
    const button = event.currentTarget;
    const font = button.dataset.engravingFont;

    this.selectFont(font, button);
  }

  /**
   * Select a font and update UI
   * @param {string} font - The font to select ('script' or 'block')
   * @param {HTMLElement} button - The button element that was clicked (optional)
   */
  selectFont(font, button = null) {
    // Update state
    this.selectedFont = font;

    // Update button active states
    if (this.fontButtons) {
      this.fontButtons.forEach(btn => {
        btn.classList.remove('engraving-font-btn--active');
        btn.setAttribute('aria-pressed', 'false');
      });
    }

    // Find the button if not provided
    if (!button) {
      button = this.selectorContainer.querySelector(`[data-engraving-font="${font}"]`);
    }

    // Add active class to selected button
    if (button) {
      button.classList.add('engraving-font-btn--active');
      button.setAttribute('aria-pressed', 'true');
    }

    // Sync font dropdown
    this.syncFontDropdown(font);

    // Update font form input for cart (only enabled if letter is selected)
    this.updateFontFormInput();

    // Update overlay if a letter is selected
    if (this.selectedLetter) {
      this.updateOverlay();
    }

    // Dispatch custom event
    this.dispatchFontChangeEvent();
  }

  /**
   * Update the hidden font form input for cart line item property
   * Font Style is only added to cart if a letter is selected
   */
  updateFontFormInput() {
    const fontInput = document.querySelector('.engraving-font-input');
    if (fontInput) {
      // Only enable font input if a letter is selected
      if (this.selectedFont && this.selectedLetter) {
        // Capitalize first letter for display (Script, Block)
        const displayValue = this.selectedFont.charAt(0).toUpperCase() + this.selectedFont.slice(1);
        fontInput.value = displayValue;
        fontInput.disabled = false;
      } else {
        // No letter selected - don't include font in cart
        fontInput.value = '';
        fontInput.disabled = true;
      }
    }
  }

  /**
   * Sync the mobile font dropdown with button selection
   * @param {string} font - The selected font
   */
  syncFontDropdown(font) {
    const dropdown = this.selectorContainer.querySelector('[data-engraving-font-dropdown]');
    if (dropdown) {
      dropdown.value = font;
    }
  }

  /**
   * Dispatch a custom event when font selection changes
   */
  dispatchFontChangeEvent() {
    const event = new CustomEvent('engraving:fontChange', {
      bubbles: true,
      detail: {
        font: this.selectedFont
      }
    });
    document.dispatchEvent(event);
  }

  /**
   * Select a letter and update UI
   * @param {string} letter - The letter to select ('none' or 'a'-'z')
   * @param {HTMLElement} button - The button element that was clicked
   */
  selectLetter(letter, button) {
    // Update state
    this.selectedLetter = letter === 'none' ? null : letter;

    // Update button active states
    this.selectButton(button);

    // Sync dropdown with button selection
    this.syncDropdown(letter);

    // Update font form input (font is only added to cart if letter is selected)
    this.updateFontFormInput();

    // Update overlay on all media items
    this.updateOverlay();

    // Update form input for cart
    this.updateFormInput();

    // Dispatch custom event for other scripts to listen to
    this.dispatchSelectionEvent();
  }

  /**
   * Update button active states
   * @param {HTMLElement} activeButton - The button to mark as active
   */
  selectButton(activeButton) {
    // Remove active class from all buttons
    this.letterButtons.forEach(btn => {
      btn.classList.remove('engraving-letter-btn--active');
      btn.setAttribute('aria-pressed', 'false');
    });

    // Add active class to selected button
    if (activeButton) {
      activeButton.classList.add('engraving-letter-btn--active');
      activeButton.setAttribute('aria-pressed', 'true');
    }
  }

  /**
   * Sync the mobile dropdown with button selection
   * @param {string} letter - The selected letter or 'none'
   */
  syncDropdown(letter) {
    const dropdown = this.selectorContainer.querySelector('[data-engraving-dropdown]');
    if (dropdown) {
      dropdown.value = letter;
    }
  }

  /**
   * Update overlay images on first media item only
   */
  updateOverlay() {
    // Find all overlay containers - check gallery first, then entire document
    let overlayContainers = this.gallery.querySelectorAll('.engraving-overlay-container');

    // If not found in gallery, try document-wide (for Liquid-added containers)
    if (overlayContainers.length === 0) {
      overlayContainers = document.querySelectorAll('.engraving-overlay-container');
    }

    // Only process the first overlay container (first product media)
    const firstContainer = overlayContainers[0];
    if (!firstContainer) return;

    const image = firstContainer.querySelector('.engraving-overlay-image');
    if (!image) return;

    // Hide all other overlay containers
    for (let i = 1; i < overlayContainers.length; i++) {
      const otherImage = overlayContainers[i].querySelector('.engraving-overlay-image');
      if (otherImage) {
        otherImage.style.display = 'none';
        otherImage.style.opacity = '0';
        otherImage.src = '';
      }
    }

    if (this.selectedLetter) {
      // Show overlay with selected letter on first media only
      if(this.selectedLetter == "o"){
        console.log("True this.selectedLetter: "+this.selectedLetter);
      }
      const imageUrl = this.getLetterImageUrl(this.selectedLetter);

      // Load new image
      const tempImage = new Image();
      tempImage.onload = () => {
        // Set image source and show it immediately
        image.src = imageUrl;

        // adjust o from left to center
        if(this.selectedLetter == "o"){
          image.style.cssText = `
            display: block !important;
            opacity: 0.75 !important;
            max-width: 50%;
            max-height: 50%;
            width: auto;
            height: auto;
            object-fit: contain;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) scale(${this.getOverlayScale()});
            pointer-events: none;
            z-index: 10;
          `;
          // adjust the letter g from bottom to top
        } else if(this.selectedLetter == "g"){
          image.style.cssText = `
            display: block !important;
            opacity: 0.75 !important;
            max-width: 50%;
            max-height: 50%;
            width: auto;
            height: auto;
            object-fit: contain;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) scale(${this.getOverlayScale()});
            pointer-events: none;
            z-index: 10;
          `;
        } else {
          image.style.cssText = `
            display: block !important;
            opacity: 0.75 !important;
            max-width: 50%;
            max-height: 50%;
            width: auto;
            height: auto;
            object-fit: contain;
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%) scale(${this.getOverlayScale()});
            pointer-events: none;
            z-index: 10;
          `;
        }
      };
      tempImage.onerror = () => {
        image.style.display = 'none';
      };
      tempImage.src = imageUrl;
    } else {
      // Hide overlay (No Engraving selected)
      image.style.display = 'none';
      image.style.opacity = '0';
      image.src = '';
    }
  }

  /**
   * Get the URL for a letter's PNG image
   * @param {string} letter - The letter (a-z)
   * @returns {string} - The image URL
   */
  getLetterImageUrl(letter) {
    const normalizedLetter = letter.toLowerCase();
    // Use different image prefix for rectangle products
    // Rectangle: rec-script-a.png or rec-block-a.png
    // Default: letter-script-a.png or letter-block-a.png
    const prefix = this.isRectangleProduct ? 'rec' : 'letter';
    return `${this.config.assetUrlBase}${prefix}-${this.selectedFont}-${normalizedLetter}.png`;
  }

  /**
   * Update the hidden form input for cart line item property
   */
  updateFormInput() {
    if (!this.formInput) {
      // Try to find it again in case it was dynamically added
      this.formInput = document.querySelector(this.config.formInputSelector);
    }

    if (this.formInput) {
      if (this.selectedLetter) {
        this.formInput.value = this.selectedLetter.toUpperCase();
        this.formInput.disabled = false;
      } else {
        this.formInput.value = '';
        this.formInput.disabled = true;
      }
    }
  }

  /**
   * Handle variant change events from Shopify PubSub
   * @param {Object} data - Event data from PubSub
   */
  handleVariantChange(data) {
    // After variant changes, we need to re-inject overlay containers
    // and restore the selected letter overlay

    // Small delay to ensure DOM has updated
    setTimeout(() => {
      this.injectOverlayContainers();

      // Re-apply the current selection
      if (this.selectedLetter) {
        this.updateOverlay();
      }
    }, 100);
  }

  /**
   * Dispatch a custom event when letter selection changes
   */
  dispatchSelectionEvent() {
    const displayValue = this.selectedLetter ? this.selectedLetter.toUpperCase() : 'None';

    const event = new CustomEvent('engraving:letterChange', {
      bubbles: true,
      detail: {
        letter: this.selectedLetter,
        displayValue: displayValue
      }
    });
    document.dispatchEvent(event);

    // Also directly update the selected display element as a fallback
    const displayElement = document.querySelector('[data-selected-display]');
    if (displayElement) {
      displayElement.textContent = displayValue;
    }
  }

  /**
   * Programmatically select a letter
   * @param {string} letter - The letter to select ('none' or 'a'-'z')
   */
  setLetter(letter) {
    const normalizedLetter = letter.toLowerCase();
    const button = this.selectorContainer.querySelector(`[data-engraving-letter="${normalizedLetter}"]`);

    if (button) {
      this.selectLetter(normalizedLetter, button);
    }
  }

  /**
   * Get the currently selected letter
   * @returns {string|null} - The selected letter or null if none
   */
  getLetter() {
    return this.selectedLetter;
  }

  /**
   * Update overlay position dynamically
   * @param {Object} position - Position configuration
   * @param {string} position.top - CSS top value
   * @param {string} position.left - CSS left value
   * @param {number} position.scale - Scale factor
   */
  setOverlayPosition({ top, left, scaleRound, scaleRectangle }) {
    if (top) this.config.overlayPositionTop = top;
    if (left) this.config.overlayPositionLeft = left;
    if (scaleRound) this.config.overlayScaleRound = scaleRound;
    if (scaleRectangle) this.config.overlayScaleRectangle = scaleRectangle;

    // Update existing overlay containers
    const containers = this.gallery.querySelectorAll('.engraving-overlay-container');
    containers.forEach(container => {
      if (top) container.style.top = top;
      if (left) container.style.left = left;
      if (scaleRound || scaleRectangle || top || left) {
        container.style.transform = `translate(-50%, -50%) scale(${this.getOverlayScale()})`;
      }
    });
  }

  /**
   * Cleanup and destroy the overlay system
   */
  destroy() {
    // Remove letter button event listeners
    if (this.letterButtons) {
      this.letterButtons.forEach(button => {
        button.removeEventListener('click', this.handleLetterSelect);
      });
    }

    // Remove font button event listeners
    if (this.fontButtons) {
      this.fontButtons.forEach(button => {
        button.removeEventListener('click', this.handleFontSelect);
      });
    }

    // Unsubscribe from variant changes
    if (this.variantChangeUnsubscriber) {
      this.variantChangeUnsubscriber();
    }

    // Remove overlay containers
    const containers = document.querySelectorAll('.engraving-overlay-container');
    containers.forEach(container => container.remove());

    // Reset state
    this.selectedLetter = null;
    this.selectedFont = 'script';
    this.isInitialized = false;
  }
}

/**
 * Auto-initialize on DOMContentLoaded if engraving selector exists
 */
document.addEventListener('DOMContentLoaded', () => {
  const selectorElement = document.getElementById('engraving-letter-selector');

  if (selectorElement) {
    // Create global instance
    window.engravingOverlay = new EngravingOverlay();
    window.engravingOverlay.init();
  }
});

// Export for module usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = EngravingOverlay;
}
